import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { FilterKeyType } from '@ocean/types/dist/filter'
import { CommonUtils, PaginationUtils } from '@ocean/utils'
import { Card } from 'types/card'
import { createApiAction } from 'api'
import { CardUtils } from 'utils'
import { userLoggedOut } from 'core/session/slices/session'
import { getElearningCategory } from 'modules/trainingLibrary/Elearning/api/elearningService'
import {
  ElearningCategoryFilterItem,
  ElearningCategoryFilterItems,
} from 'modules/trainingLibrary/Elearning/types'
import { ElearningState, ApiElearningCategoryResponse } from './types'

export const FILTER_TARGET_GROUP_BELONGS_TO_ALL_VALUE = 'All target groups'

const initialState: ElearningState = {
  elearningCategoryData: {},
  elearningCategoryFilters: {
    status: { keyType: FilterKeyType.Number },
    targetGroup: { keyType: FilterKeyType.Array },
    type: { keyType: FilterKeyType.Number },
  },
}

const elearningSlice = createSlice({
  name: 'elearning',
  initialState,
  reducers: {
    elearningCategoryDataLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiElearningCategoryResponse>>
    ) {
      state.elearningCategoryData = action.payload
    },
    clearElearningCategory(state) {
      state.elearningCategoryData = initialState.elearningCategoryData
    },
    setElearningCategoryFilters(state, action: PayloadAction<ElearningCategoryFilterItem>) {
      const filter = state.elearningCategoryFilters[action.payload.name]
      if (filter) filter.value = action.payload.value
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const { elearningCategoryDataLoaded, setElearningCategoryFilters, clearElearningCategory } =
  elearningSlice.actions

export { setElearningCategoryFilters, clearElearningCategory }
export default elearningSlice.reducer

export const getElearningCategoryCardsPartition = createSelector(
  (elearningCategoryCards: Card[] = []) => elearningCategoryCards,
  elearningCategoryCards =>
    CommonUtils.partition<Card>(elearningCategoryCards, CardUtils.checkElearningCategoryCardType)
)

export const getElearningCategoryElearningListPageItems = createSelector(
  (elearningCategoryCards: Card[] = [], currentPageId: number, currentPageSize: number) => ({
    elearningCategoryCardPartition: getElearningCategoryCardsPartition(elearningCategoryCards),
    currentPageId,
    currentPageSize,
  }),
  ({ elearningCategoryCardPartition, currentPageId, currentPageSize }) => {
    const [, elearningCards] = elearningCategoryCardPartition
    return PaginationUtils.getPageItems<Card>(elearningCards, currentPageId, currentPageSize)
  }
)

export const loadElearningCategoryData = (
  categoryId: Guid,
  externalFilters: ElearningCategoryFilterItems
) => {
  return createApiAction({
    apiCalls: {
      elearningCategory: () => getElearningCategory(categoryId, externalFilters),
    },
    payloadAction: elearningCategoryDataLoaded,
    shouldShowGlobalLoading: false,
    getResultToKeep: ({ elearning }) => elearning.elearningCategoryData.result,
  })
}
